import React, { useCallback, useRef, useState } from 'react';
import Modal from './Modal';

/**
 * The app's own confirmation dialog, and the hook that makes it a drop-in
 * replacement for `window.confirm`.
 *
 * WHY. Destructive actions were split down the middle: five places used the
 * styled `Modal`, seventeen used `window.confirm`. So "delete this user" looked
 * like the product on the Users list and like a browser alert on Tiers, Models,
 * Coupons, Channels and everywhere else. That is not only inconsistent —
 * `window.confirm` cannot be styled or branded, Chrome prefixes it with "This
 * page says", which reads as something untrusted having hijacked the tab, and it
 * renders nothing but plain text. Three call sites were already paying for that
 * last part: Templates embedded a literal `\n` and `{{name}}` in its message,
 * Channels and Maintenance concatenated paragraphs with `\n\n`.
 *
 * It also blocks the entire tab synchronously, which on a page that polls (agent
 * status every 10s) means the poll stops while the dialog is open.
 *
 * SHAPE. `useConfirm` returns `[confirm, dialog]`:
 *
 *     const [confirm, confirmDialog] = useConfirm();
 *
 *     const remove = async (row) => {
 *       if (!await confirm({
 *         title: `Delete ${row.name}?`,
 *         body: <p>This cannot be undone.</p>,
 *         confirmLabel: 'Delete',
 *       })) return;
 *       ...
 *     };
 *
 *     return (<div>…{confirmDialog}</div>);
 *
 * `confirm` returns a promise, so the call sites keep their existing
 * `if (!confirm(...)) return;` shape with `await` added — which is what keeps
 * this a presentation change across seventeen files rather than a rewrite of
 * seventeen control flows.
 *
 * Options:
 *   - title        heading. Ask the question here, in one line.
 *   - body         nodes. The consequence, structured.
 *   - confirmLabel default 'Confirm'. Name the verb and the object.
 *   - cancelLabel  default 'Cancel'.
 *   - tone         'danger' (default) or 'primary'. Destructive is the common
 *                  case, so it is the default; `primary` is for a confirmation
 *                  that is merely significant, like arming a freeze.
 *
 * Escape and a backdrop click both resolve FALSE, matching `window.confirm`'s
 * dismissal semantics — a dialog that resolved nothing on Escape would leave the
 * caller awaiting a promise forever.
 */
export function useConfirm() {
  const [request, setRequest] = useState(null);
  const resolver = useRef(null);

  const settle = useCallback((answer) => {
    const resolve = resolver.current;
    resolver.current = null;
    setRequest(null);
    resolve?.(answer);
  }, []);

  const confirm = useCallback((options) => new Promise((resolve) => {
    // A second call while one is open would orphan the first promise. Answer it
    // false and take over, rather than leaving a caller hanging.
    resolver.current?.(false);
    resolver.current = resolve;
    setRequest(options || {});
  }), []);

  const dialog = request ? (
    <Modal open onClose={() => settle(false)} title={request.title} maxWidth={460}>
      <div className="stack gap-2">{request.body}</div>
      <div className="modal-footer">
        <button type="button" className="btn btn-secondary" onClick={() => settle(false)}>
          {request.cancelLabel || 'Cancel'}
        </button>
        <button
          type="button"
          className={`btn btn-${request.tone === 'primary' ? 'primary' : 'danger'}`}
          onClick={() => settle(true)}
        >
          {request.confirmLabel || 'Confirm'}
        </button>
      </div>
    </Modal>
  ) : null;

  return [confirm, dialog];
}

export default useConfirm;
